import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { getPriceCascades } from '@/services/api';
import type { PriceCascade } from '@/types/price-rules';

// Cascata e price rule nunca disputam o mesmo anúncio: no rocket-script, o bloco
// que tem cascata usa os steps dela e ignora a price rule (contentSlots.ts,
// reward.ts...). Mas isso é invisível no painel — a regra parece valer no site
// todo, e o PMR que o sync mede inclui os blocos de cascata, que falham de
// propósito nos degraus altos e puxam o PMR para baixo.

export interface CascadeBlockSummary {
    adUnit: string;
    allPages: boolean;
    posts: number;
    countries: string[];
}

export function summarizeCascades(cascades: PriceCascade[] | undefined): CascadeBlockSummary[] {
    const byUnit = new Map<string, CascadeBlockSummary>();
    for (const c of cascades ?? []) {
        if (c.status !== 'active') continue;
        const s = byUnit.get(c.ad_unit) ?? { adUnit: c.ad_unit, allPages: false, posts: 0, countries: [] };
        if (c.id_post_wp) s.posts += 1; else s.allPages = true;
        if (!s.countries.includes(c.country)) s.countries.push(c.country);
        byUnit.set(c.ad_unit, s);
    }
    return [...byUnit.values()];
}

export function useCascadeSummary(domainId: number | null | undefined) {
    const { data, isLoading } = useQuery({
        queryKey: ['price-cascades', domainId],
        queryFn: () => getPriceCascades(domainId!), // habilitado só com domainId
        enabled: !!domainId,
        staleTime: 60_000,
    });
    return { summary: summarizeCascades(data), isLoading };
}

/** Blocos com cascata que a regra (ad_unit) alcança. '*' alcança todos. */
export function overlappingBlocks(summary: CascadeBlockSummary[], ruleAdUnit: string | undefined) {
    if (!ruleAdUnit || ruleAdUnit === '*') return summary;
    return summary.filter(s => s.adUnit === ruleAdUnit);
}

export function describeBlock(s: CascadeBlockSummary) {
    const where = s.allPages ? 'todas as páginas' : `${s.posts} post${s.posts !== 1 ? 's' : ''}`;
    const countries = s.countries.includes('*') ? '' : ` · ${s.countries.join(', ')}`;
    return `${s.adUnit} (${where}${countries})`;
}

const CASCADES_URL = '/price-cascades';

interface Props {
    domainId: number | null | undefined;
    adUnit?: string;
    allAdUnits?: string[];
}

/** Aviso do passo 1: o site tem cascata ativa. */
export function CascadeDomainWarning({ domainId }: Props) {
    const { summary } = useCascadeSummary(domainId);
    if (summary.length === 0) return null;

    return (
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 space-y-1">
                <p className="font-semibold">Este site tem cascata ativa</p>
                <p>
                    {summary.map(describeBlock).join(' · ')}.
                </p>
                <p className="text-amber-800">
                    Nesses blocos e páginas a <b>cascata define o preço</b> e a price rule não vale — as duas não
                    disputam o mesmo anúncio. A regra só age onde não há cascata.
                </p>
                <a href={CASCADES_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-amber-700 hover:text-amber-900 underline">
                    Gerenciar cascatas <ExternalLink className="h-3.5 w-3.5" />
                </a>
            </div>
        </div>
    );
}

/** Aviso do passo 2: o bloco escolhido cruza com cascata e contamina o PMR. */
export function CascadeSegmentWarning({ domainId, adUnit, allAdUnits = [] }: Props) {
    const { summary } = useCascadeSummary(domainId);
    const overlap = overlappingBlocks(summary, adUnit);
    if (overlap.length === 0) return null;

    const withCascade = new Set(summary.map(s => s.adUnit));
    const free = allAdUnits.filter(u => !withCascade.has(u));

    return (
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 space-y-1">
                <p className="font-semibold">
                    {adUnit && adUnit !== '*' ? `${adUnit} tem cascata ativa neste site` : 'Todos os blocos inclui blocos com cascata'}
                </p>
                <p>
                    Em {overlap.map(describeBlock).join(', ')} a cascata define o preço e esta regra não vale.
                    O <b>PMR consultado no GAM inclui esses blocos</b>, que falham de propósito nos degraus altos —
                    o PMR sai mais baixo do que o real e o sync tende a derrubar o price.
                </p>
                {free.length > 0 && (
                    <p className="text-amber-800">Blocos sem cascata neste site: {free.join(', ')}.</p>
                )}
            </div>
        </div>
    );
}

/** Selo da lista de regras. */
export function CascadeBadge({ summary, adUnit }: { summary: CascadeBlockSummary[]; adUnit: string }) {
    const overlap = overlappingBlocks(summary, adUnit);
    if (overlap.length === 0) return null;
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200"
            title={`Cascata ativa em ${overlap.map(describeBlock).join(', ')}. Ali a cascata define o preço e esta regra não vale; o PMR medido inclui esses blocos.`}
        >
            <AlertTriangle className="h-3 w-3" /> cascata ativa
        </span>
    );
}
